import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  CurrencyRupeeIcon,
  UserGroupIcon,
  TruckIcon,
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format, subDays } from 'date-fns';

const COLORS = {
  primary: '#FF8C00',
  secondary: '#FF6B35',
  success: '#10b981',
  danger: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
  purple: '#a855f7',
  blue: '#3b82f6',
  green: '#10b981',
  orange: '#FF8C00',
  gray: '#6b7280'
};

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7days'); // 7days, 30days, 90days
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalAgents: 0,
    totalSellers: 0,
    totalCars: 0,
    activeCars: 0,
    soldCars: 0
  });
  const [revenueData, setRevenueData] = useState([]);
  const [agentData, setAgentData] = useState([]);
  const [sellerData, setSellerData] = useState([]);
  const [carStatusData, setCarStatusData] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      
      // Fetch all data
      const [agentsRes, sellersRes, carsRes, txRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/sellers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/cars`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        // Revenue must come from actual transactions, never be estimated.
        axios.get(`${process.env.REACT_APP_API_URL}/api/admin/transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { data: [] } }))
      ]);

      const agents = agentsRes.data.data || agentsRes.data || [];
      const sellers = sellersRes.data.data || sellersRes.data || [];
      const cars = carsRes.data.data || carsRes.data || [];
      const txRaw = txRes?.data?.data ?? txRes?.data ?? [];
      const transactions = Array.isArray(txRaw) ? txRaw : (txRaw.transactions || []);

      // Calculate stats
      const activeCars = cars.filter(c => c.status === 'available' || c.status === 'active').length;
      const soldCars = cars.filter(c => c.status === 'sold').length;
      // Sum of completed payments. Previously this multiplied sold cars by a
      // flat 25,000, which reported income the business never received.
      const totalRevenue = transactions
        .filter((t) => ['completed', 'success', 'paid'].includes(String(t?.status || '').toLowerCase()))
        .reduce((sum, t) => sum + (Number(t?.amount) || 0), 0);

      setStats({
        totalRevenue,
        totalAgents: agents.length,
        totalSellers: sellers.length,
        totalCars: cars.length,
        activeCars,
        soldCars
      });

      // Revenue trend from real transactions. This previously generated random
      // figures, so the chart showed income that did not exist.
      const days = timeRange === '7days' ? 7 : timeRange === '30days' ? 30 : 90;
      const revenue = [];

      // Bucket completed transactions by day.
      const byDay = {};
      (Array.isArray(transactions) ? transactions : []).forEach((t) => {
        const when = t?.createdAt || t?.paidAt || t?.date;
        if (!when) return;
        const key = format(new Date(when), 'MMM dd');
        if (!byDay[key]) byDay[key] = { revenue: 0, transactions: 0 };
        byDay[key].revenue += Number(t?.amount) || 0;
        byDay[key].transactions += 1;
      });

      for (let i = days - 1; i >= 0; i--) {
        const date = subDays(new Date(), i);
        const key = format(date, 'MMM dd');
        revenue.push({
          date: key,
          revenue: byDay[key]?.revenue || 0,
          transactions: byDay[key]?.transactions || 0,
        });
      }
      setRevenueData(revenue);

      // Agent performance data
      const agentPerf = [
        { name: 'Active', value: agents.filter(a => a.isActive).length },
        { name: 'Inactive', value: agents.filter(a => !a.isActive).length },
        { name: 'Pending', value: agents.filter(a => !a.isActive && a.status === 'pending').length }
      ];
      setAgentData(agentPerf);

      // Seller distribution data
      const sellerDist = [
        { name: 'Individual', value: sellers.filter(s => s.sellerType === 'individual').length },
        { name: 'Agent-Created', value: sellers.filter(s => s.sellerType === 'agent-created').length },
        { name: 'Dealer', value: sellers.filter(s => s.sellerType === 'dealer').length }
      ];
      setSellerData(sellerDist.filter(s => s.value > 0));

      // Car status distribution
      const carStatus = [
        { name: 'Active', value: activeCars },
        { name: 'Sold', value: soldCars },
        { name: 'Blocked', value: cars.filter(c => c.status === 'blocked').length },
        { name: 'Pending', value: cars.filter(c => c.status === 'pending').length }
      ];
      setCarStatusData(carStatus.filter(c => c.value > 0));

    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, trend, color = 'primary' }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          {trend && (
            <div className={`flex items-center mt-2 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? (
                <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
              ) : (
                <ArrowTrendingDownIcon className="w-4 h-4 mr-1" />
              )}
              <span>{Math.abs(trend)}% vs last period</span>
            </div>
          )}
        </div>
        <div className={`p-4 bg-${color}-100 rounded-xl`}>
          <Icon className={`w-8 h-8 text-${color}-600`} />
        </div>
      </div>
    </motion.div>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === 'number' && entry.dataKey === 'revenue' 
                ? `₹${(entry.value / 1000).toFixed(1)}K` 
                : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        className="font-semibold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="text-3xl font-display font-bold text-gray-900">Analytics & Reports</h1>
          <p className="text-gray-600 mt-2">
            Comprehensive analytics dashboard with real-time insights
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center space-x-4 mt-4 lg:mt-0"
        >
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
          </select>
          <button
            onClick={fetchAnalytics}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </motion.div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Revenue"
          value={`₹${(stats.totalRevenue / 100000).toFixed(1)}L`}
          icon={CurrencyRupeeIcon}
          color="primary"
        />
        <StatCard
          title="Total Agents"
          value={stats.totalAgents}
          icon={UserGroupIcon}
          color="blue"
        />
        <StatCard
          title="Total Sellers"
          value={stats.totalSellers}
          icon={UserGroupIcon}
          color="green"
        />
        <StatCard
          title="Cars Sold"
          value={stats.soldCars}
          icon={TruckIcon}
          color="orange"
        />
      </div>

      {/* Revenue Trend Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Revenue Trend</h3>
        {loading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={revenueData}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#888" />
              <YAxis tick={{ fontSize: 12 }} stroke="#888" />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke={COLORS.primary}
                fillOpacity={1}
                fill="url(#colorRevenue)"
                name="Revenue (₹)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </motion.div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Agent Analytics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Agent Distribution</h3>
          {loading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={agentData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {agentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={[COLORS.success, COLORS.gray, COLORS.warning][index % 3]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Seller Analytics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Seller Distribution</h3>
          {loading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={sellerData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#888" />
                <YAxis tick={{ fontSize: 12 }} stroke="#888" />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="value" name="Sellers" fill={COLORS.green} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Car Status Analytics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Car Status Distribution</h3>
          {loading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={carStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {carStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={[COLORS.success, COLORS.gray, COLORS.danger, COLORS.warning][index % 4]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Transaction Volume */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Transaction Volume</h3>
          {loading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#888" />
                <YAxis tick={{ fontSize: 12 }} stroke="#888" />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="transactions"
                  stroke={COLORS.blue}
                  strokeWidth={2}
                  name="Transactions"
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </motion.div>
      </div>

      {/* Summary Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Quick Stats</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-3xl font-bold text-blue-600">{stats.totalCars}</div>
            <div className="text-sm text-gray-600 mt-1">Total Cars</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-3xl font-bold text-green-600">{stats.activeCars}</div>
            <div className="text-sm text-gray-600 mt-1">Active Listings</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-3xl font-bold text-orange-600">{stats.soldCars}</div>
            <div className="text-sm text-gray-600 mt-1">Cars Sold</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-3xl font-bold text-purple-600">
              {stats.totalCars > 0 ? ((stats.soldCars / stats.totalCars) * 100).toFixed(1) : 0}%
            </div>
            <div className="text-sm text-gray-600 mt-1">Conversion Rate</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Analytics;
